/**
 * Cache keys for auth data
 */
export const AUTH_CACHE_KEYS = {
  USER_INFO: "/user-info",
  AUTH_GROUPS: "/auth-groups",
};

/**
 * Cache keys for class data
 */
export const CLASSES_CACHE_KEYS = {
  CLASSES: "/classes",
  CLASS_BY_ID: (id: string) => `/class/${id}`,
  CLASSES_BY_TUTOR_ID: (tutorId: string) => `/classes/tutor/${tutorId}`,
  CLASSES_BY_STATUS: (status: boolean) => `/classes/status/${status}`,
};

/**
 * Cache keys for client data
 */
export const CLIENTS_CACHE_KEYS = {
  CLIENTS: "/clients",
  USERS: "/users",
  CLIENT: (id: string) => `/clients/${id}`,
  MY_CLIENT_DATA: (sub: string) => `me/client/${sub}`,
};

/**
 * Cache keys for family data
 */
export const FAMILIES_CACHE_KEYS = {
  FAMILIES: "/families",
  FAMILY: (id: number) => `/families/${id}`,
  FAMILY_BY_USER: (id: string) => `/families/user/${id}`,
};

/**
 * Cache keys for configuration data
 */
export const CONFIGURATIONS_CACHE_KEYS = {
  CONFIGURATIONS: "/configurations",
};

/**
 * Cache keys for event data
 */
export const EVENTS_CACHE_KEYS = {
  EVENTS: "/events",
  EVENTS_ALL: "/events/all",
  EVENT_BY_ID: (id: string) => `/event/${id}`,
  EVENTS_BY_USER_ID: (userId: string, startDate?: string, endDate?: string) =>
    `/events/user/${userId}${startDate ? `/from/${startDate}` : ""}${
      endDate ? `/to/${endDate}` : ""
    }`,
  EVENT_DETAILS_BY_USER_ID: (userId: string, before?: string, after?: string) =>
    `/events-details/user/${userId}${before ? `/to/${before}` : ""}${
      after ? `/from/${after}` : ""
    }`,
  EVENTS_DETAILS_BY_FAMILY_ID: (
    familyId: number,
    before?: string,
    after?: string,
    is_taught?: boolean,
  ) =>
    `/events-details/family/${familyId}${before ? `/to/${before}` : ""}${
      after ? `/from/${after}` : ""
    }${is_taught ? `/taught/${is_taught}` : ""}`,
  EVENTS_BY_STAFF_ID: (staffId: string, startDate?: string, endDate?: string) =>
    `/events/staff/${staffId}${startDate ? `/from/${startDate}` : ""}${
      endDate ? `/to/${endDate}` : ""
    }`,
  EVENTS_BY_PARTICIPANT: (participantId: string, participantType: "user" | "staff") =>
    `/events/participant/${participantType}/${participantId}`,
  EVENTS_BY_SOURCE_ID: (sourceId: string) => `/events/source/${sourceId}`,
  EVENTS_BY_SESSION_TYPE: (sessionType: string) => `/events/type/${sessionType}`,
  EVENTS_BY_ATTENDANCE: (attendance: string) => `/events/attendance/${attendance}`,
  EVENTS_BY_TAUGHT_STATUS: (isTaught: boolean) => `/events/taught/${isTaught}`,
  EVENTS_BY_TRANSACTION_ID: (transactionId: string) => `/events/transaction/${transactionId}`,
  EVENT_PARTICIPANTS: (eventId: number) => `/event/${eventId}/participants`,
};

/**
 * Cache keys for files data
 */
export const FILES_CACHE_KEYS = {
  FILES: (bucket: string) => `/files/?bucket=${bucket}`,
};

/**
 * Cache keys for inquiry data
 */
export const INQUIRIES_CACHE_KEYS = {
  INQUIRIES: (staffId?: string) => `/inquiries?${JSON.stringify(staffId)}`,
  INQUIRY: (id: string) => `/inquiries/${id}`,
};

/**
 * Cache keys for notification rule data
 */
export const NOTIFICATION_RULES_CACHE_KEYS = {
  NOTIFICATION_RULES: "/notification-rules",
  NOTIFICATION_RULE: (id: string) => `/notification-rules/${id}`,
};

/**
 * Cache keys for notification data
 */
export const NOTIFICATIONS_CACHE_KEYS = {
  ALL_NOTIFICATIONS: (is_read = false) => `/notifications/all?is_read=${is_read}`,
  ADMIN_NOTIFICATIONS: (is_read = false) => `/notifications/admin?is_read=${is_read}`,
  TUTOR_NOTIFICATIONS: (tutorId: string, is_read = false) =>
    `/notifications/tutor/${tutorId}?is_read=${is_read}`,
  CLIENT_NOTIFICATIONS: (clientId: string, is_read = false) =>
    `/notifications/client/${clientId}?is_read=${is_read}`,
};

/**
 * Cache keys for public data
 */
export const PUBLIC_DATA_CACHE_KEYS = {
  CLASSES: "/classes",
  SUBJECTS: "/subjects",
  TUTORS: "/tutors/public",
  TUTOR: (id: string) => `/tutors/${id}`,
};

/**
 * Cache keys for service types data
 */
export const SERVICE_TYPES_CACHE_KEYS = {
  SERVICE_TYPES: "/service-types",
};

/**
 * Cache keys for staff data
 */
export const STAFF_CACHE_KEYS = {
  TUTORS: "/tutors",
  TUTOR: (id: string) => `/tutors/${id}`,
  MY_TUTOR_DATA: (staffId: string) => `me/tutor/${staffId}`,
};

/**
 * Cache keys for student data
 */
export const STUDENTS_CACHE_KEYS = {
  STUDENTS: "/students",
  STUDENT: (id: string) => `/students/${id}`,
  STUDENTS_BY_CLIENT: (clientId: string) => `/clients/${clientId}/students`,
};

/**
 * Cache keys for subject data
 */
export const SUBJECTS_CACHE_KEYS = {
  SUBJECTS: "/subjects",
};

/**
 * Cache keys for transaction data
 */
export const TRANSACTIONS_CACHE_KEYS = {
  TRANSACTIONS: "/transactions",
  TRANSACTION_BY_ID: (id: string) => `/transaction/${id}`,
  TRANSACTIONS_BY_USER_ID: (userId: string) => `/transactions/user/${userId}`,
  TRANSACTIONS_BY_STAFF_ID: (staffId: string) => `/transactions/staff/${staffId}`,
  TRANSACTIONS_BY_STATUS: (status: string) => `/transactions/status/${status}`,
  BY_PROCESSOR_ID: (transactionId: string) => `/transactions/transactionId/${transactionId}`,
  BY_FAMILY_ID: (familyId: number) => `/transactions/family/${familyId}`,
};

/**
 * Cache keys for application data
 */
export const TUTOR_APPLICATIONS_CACHE_KEYS = {
  APPLICATIONS: "/tutor-applications",
  APPLICATION: (id: string) => `/tutor-applications/${id}`,
};

/**
 * Cache keys for analytics
 */
export const ANALYTICS_CACHE_KEYS = {
  FEEDBACK: "/analytics/feedback",
  USER_NOTIFICATIONS: (userId: string) => `/analytics/user/${userId}/notifications`,
};
